import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  Menu,
  PatchAdminV1StoresStoreIdMenusIdBody,
  PostAdminV1StoresStoreIdMenusBody,
  PutAdminV1StoresStoreIdMenusIdItemsBody,
} from '@nucleus-commerce/api-client/admin';

import { menusApi } from '../api/menus-api';

import { menuKeys } from './menu-keys';

function remember(queryClient: ReturnType<typeof useQueryClient>, storeId: string, menu: Menu) {
  queryClient.setQueryData(menuKeys.detail(storeId, menu.id), menu);
  return queryClient.invalidateQueries({ queryKey: menuKeys.list(storeId) });
}

/** 409 on `handle`; 422 LIMIT_EXCEEDED past 20 menus. */
export function useCreateMenu(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdMenusBody) => menusApi.create(storeId, body),
    meta: { successMessage: 'menus:toasts.created', silentError: true },
    onSuccess: (menu) => remember(queryClient, storeId, menu),
  });
}

export function useUpdateMenu(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdMenusIdBody }) =>
      menusApi.update(storeId, id, body),
    meta: { successMessage: 'menus:toasts.updated', silentError: true },
    onSuccess: (menu) => remember(queryClient, storeId, menu),
  });
}

/** 422 on `items[i]...url` / `.targetId`, 400 for a fourth level; the tree editor shows both. */
export function useReplaceMenuItems(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PutAdminV1StoresStoreIdMenusIdItemsBody }) =>
      menusApi.replaceItems(storeId, id, body),
    meta: { successMessage: 'menus:toasts.itemsSaved', silentError: true },
    onSuccess: (menu) => remember(queryClient, storeId, menu),
  });
}

export function useDeleteMenu(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => menusApi.remove(storeId, id),
    meta: { successMessage: 'menus:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: menuKeys.detail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: menuKeys.list(storeId) });
    },
  });
}
